import {computed, inject, Injectable, NgZone, Signal, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  catchError,
  defer,
  EMPTY,
  from,
  map,
  Observable, of,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
  timer,
} from "rxjs";
import {ApiService} from "./api.service";
import {AuthTokenResponse, User} from "@supabase/supabase-js";
import {UserData} from "../features/auth/login/login.component";
import {Router} from "@angular/router";
import {StorageService} from "../shared/data-access/storage.service";
import {CookieService} from "ngx-cookie-service";
import {SelectionService} from "./selection.service";
import {EventService} from "./event.service";
import {Userrole} from "../shared/interfaces/user";
import {LocalStorageService} from "../shared/data-access/local-storage.service";

interface AdditionalUserData {
  username: string,
  first_name: string,
  last_name: string,
  require_password_update: boolean,
}

export interface AppUser extends User, AdditionalUserData {
}

export type LoginStatus = "pending" | "authenticating" | "success" | "error";

interface UserState {
  user: AppUser | null;
  status: LoginStatus;
  sessionTimeout: number | null;
}

@Injectable({
  providedIn: "root",
})
export class UserService {
  private apiService = inject(ApiService);
  private eventService = inject(EventService);
  private storageService = inject(StorageService);
  private localStorageService = inject(LocalStorageService);
  private selectionService = inject(SelectionService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  Userrole = Userrole;

  private EXPIRATION_KEY = "session_expiration_time";

  private initialState: UserState = {
    user: null,
    status: "pending",
    sessionTimeout: null,
  };

  // sources
  editTimeout$ = new Subject<number>();
  login$ = new Subject<UserData>();

  loginResponse$ = new Subject<AuthTokenResponse>();
  sessionTimeout$: Observable<number | null> =
    this.storageService.refreshTrigger$.pipe(
      switchMap(() => this.apiService.getSessionTimeout()),
      shareReplay(1)
    );
  logoutTimer: Subscription | null = null;

  // state
  private state = signal<UserState>(this.initialState);

  // selectors
  user = computed(() => this.state().user);
  userrole = computed(
    () => this.state().user?.app_metadata["userrole"] as Userrole
  );
  status = computed(() => this.state().status);
  sessionTimeout = computed(() => this.state().sessionTimeout);
  isAuthenticated = computed(() => {
    return this.state().user && this.state().user?.aud === "authenticated";
  });
  isActive: Signal<boolean> = computed(() => this.state().user?.app_metadata['useractive'])
  isPasswordUpdateRequired: Signal<boolean> = computed(() => this.state().user?.require_password_update || false)
  userProductionLineId: Signal<string> = computed(() => this.state().user?.app_metadata['productionLine'].split('%')[0])
  userProductionLineName: Signal<string> = computed(() => this.state().user?.app_metadata['productionLine'].split('%')[1])

  constructor() {
    // reducer
    this.checkExistingTimer();
    this.initializeUser();

    this.sessionTimeout$
      .pipe(takeUntilDestroyed())
      .subscribe((sessionTimeout) =>
        this.state.update((state) => ({
          ...state,
          sessionTimeout,
        }))
      );

    this.login$
      .pipe(
        switchMap((credentials: UserData) => {
          return from(this.apiService.signIn(credentials));
        })
      )
      .subscribe((response: AuthTokenResponse) => {
        if (response.error) {
          this.storageService.setError(
            "Anmeldung fehlgeschlagen: Benutzername oder Passwort ungültig."
          );
        } else if (
          response.data &&
          !response.data.user.app_metadata["useractive"]
        ) {
          this.storageService.setError(
            "Ihr Konto wurde deaktiviert. Bitte wenden Sie sich an den Administrator."
          );
          this.apiService.clearUserData();
        } else {
          this.loginResponse$.next(response);
        }
      });

    this.loginResponse$
      .pipe(
        switchMap((response: AuthTokenResponse) => {
          return this.apiService.getSessionTimeout().pipe(
            map((timeout) => ({user: response.data.user, timeout})),
            catchError((error) => {
              console.error("Error fetching session timeout", error);
              return EMPTY;
            })
          );
        }),
        switchMap(({user, timeout}) =>
          this.apiService.getSingleUserById(user!.id).pipe(
            map((userData) => ({appUser: {...user, ...userData} as AppUser, timeout})),
            catchError((error) => {
              console.error("Error fetching userData", error);
              return EMPTY;
            })
          )
        ),
        tap(({appUser, timeout}) => {
          if (appUser && appUser.app_metadata?.["useractive"] && timeout) {
            this.setUser(appUser)
            this.state.update((state) => ({
              ...state,
              user: appUser,
              sessionTimeout: timeout,
            }));
            this.startLogoutTimer(timeout);
            if (appUser.app_metadata["userrole"] === Userrole.Admin) {
              this.storageService.loadUserroles();
            }
            this.router.navigate([this.getRedirectPathByRole()]);
          }
        }),
        catchError((error) => {
          console.error(
            "Unhandled or rethrown error in authentication stream:",
            error
          );
          return EMPTY;
        })
      )
      .subscribe();

    this.editTimeout$.pipe(takeUntilDestroyed()).subscribe((newTimeout) => {
      this.apiService.updateSessionTimeout(newTimeout);
      this.state.update((state) => ({
        ...state,
        sessionTimeout: newTimeout,
      }));
    });
  }

  private initializeUser() {
    const storedUser = this.apiService.getUserData()
    if (storedUser) {
      this.state.update((state) => ({
        ...state,
        user: storedUser
      }))
    }
  }

  setUser(user: AppUser) {
    this.state.update((state) => ({
      ...state,
      user
    }))
    if (user) {
      this.localStorageService.saveToLocalStorage({
        key: 'user',
        value: JSON.stringify(user)
      })
    } else {
      this.localStorageService.removeFromLocalStorage('user')
    }
  }

  private startLogoutTimer(timeout: number | null) {
    if (timeout) {
      this.clearLogoutTimer();
      const expirationTime = Date.now() + timeout * 1000;
      this.storageService.storage.setItem(
        this.EXPIRATION_KEY,
        expirationTime.toString()
      );
      this.ngZone.runOutsideAngular(() => {
        this.logoutTimer = timer(timeout * 1000)
          .pipe(take(1))
          .subscribe(() => {
            this.ngZone.run(() => this.logout());
          });
      });
    }
  }

  private checkExistingTimer() {
    const storedExpirationTime = this.storageService.storage.getItem(
      this.EXPIRATION_KEY
    );
    if (storedExpirationTime) {
      const expirationTime = parseInt(storedExpirationTime, 10);
      const now = Date.now();
      if (expirationTime > now) {
        const remainingTime = expirationTime - now;
        this.startLogoutTimer(remainingTime / 1000);
      } else {
        this.logout();
      }
    }
  }

  private clearLogoutTimer() {
    if (this.logoutTimer) {
      this.logoutTimer.unsubscribe();
      this.logoutTimer = null;
    }
  }

  login(credentials: UserData) {
    return from(defer(() => this.apiService.signIn(credentials)));
  }

  getRedirectPathByRole() {
    switch (this.userrole()) {
      case Userrole.Admin:
        return "admin/dashboard";
      case Userrole.Mitarbeiter:
        if (
          this.cookieService.get("productionLine") &&
          this.cookieService.get("workplace")
        ) {
          this.selectionService.changeProductionLine(
            this.cookieService.get("productionLine").split("%")[0]
          );
          return "dashboard";
        } else {
          return "select";
        }
      case Userrole.Teamleiter:
      case Userrole.Schichtleiter:
        this.selectionService.changeProductionLine(
          this.user()?.app_metadata?.["productionLine"].split("%")[0]
        );
        return "dashboard";
      case Userrole.Produktionsleiter:
      case Userrole.Techniker:
        this.selectionService.changeProductionLine("");
        return "dashboard";
      default:
        return "dashboard";
    }
  }

  clearState() {
    this.state.update(() => this.initialState);
  }

  logout() {
    this.apiService.clearUserData();
    this.clearState();
    this.selectionService.changeProductionLine(null);
    this.selectionService.changeWorkplace(null);
    this.clearLogoutTimer();
    this.eventService.emitLogout();
    this.router.navigate(["login"]);
  }
}
